import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Cabine } from "../componentes/FerramentaUi";
import { SetaDireita } from "../componentes/ui";
import {
  apagarCalculo,
  meusCalculos,
  type CalculoGuardado,
  type Historico,
} from "../lib/calculadoras";
import { emReais, numeroDoGov } from "../lib/montarCalculo";
import CalcRegimeGeral from "./CalcRegimeGeral";
import CalcSimplificado from "./CalcSimplificado";
import CalcBaseCalculo from "./CalcBaseCalculo";

// As Calculadoras da Reforma Tributária (000221) — card `calculadoras`, Pós RT.
//
// ---------------------------------------------------------------------------
// O que estas telas fazem, e o que elas de propósito NÃO fazem
// ---------------------------------------------------------------------------
// Elas preenchem, mandam à calculadora oficial da Receita Federal (o piloto
// CBS) e apresentam a resposta. **Nenhuma conta de tributo acontece aqui.**
// Quem responde "quanto é o IBS desta operação" é a Receita; uma conta escrita
// no hub seria a segunda definição do mesmo número e divergiria da oficial no
// primeiro ajuste de alíquota — que é o oposto do que a ferramenta existe para
// dar ao aluno.
//
// O caminho passa pela Edge Function `calcular-tributos`, e não por `fetch`
// direto, porque o navegador não alcança aquela API: medido em 14/09, o
// preflight `OPTIONS` responde 403 e a resposta simples não traz
// `access-control-allow-origin`.
//
// ---------------------------------------------------------------------------
// Os segmentos
// ---------------------------------------------------------------------------
//   /ferramenta/calculadoras                as três portas e os últimos cálculos
//   /ferramenta/calculadoras/regime-geral   Regime Regular e Simples Nacional
//   /ferramenta/calculadoras/simplificado   Split Payment Simplificado
//   /ferramenta/calculadoras/base-calculo   as bases de cálculo (CIBS e IS)
//
// Sub-ROTA, e não estado: o aluno volta pelo botão do navegador, manda o link
// de uma calculadora ao colega e recarregar não o joga para a primeira. É a
// mesma decisão do HUB DP e do Radar Simples.

export const BASE = "/ferramenta/calculadoras";

const PORTAS = [
  {
    seg: "regime-geral",
    nome: "Regime Geral",
    resumo:
      "O IBS, a CBS e o Imposto Seletivo de uma operação: NCM ou NBS, situação e classificação tributária, base de cálculo e compra governamental.",
  },
  {
    seg: "simplificado",
    nome: "Simplificado",
    resumo:
      "O Split Payment Simplificado: quanto é retido do fornecedor no pagamento e quanto sobra para ele. Vale a partir de 2027.",
  },
  {
    seg: "base-calculo",
    nome: "Base de Cálculo",
    resumo:
      "O que entra e o que sai da base do IBS/CBS e da base do Imposto Seletivo, componente por componente.",
  },
];

function telaDaSecao(secao: string) {
  if (secao === "regime-geral") return <CalcRegimeGeral />;
  if (secao === "simplificado") return <CalcSimplificado />;
  if (secao === "base-calculo") return <CalcBaseCalculo />;
  return <Entrada />;
}

export default function Calculadoras() {
  const params = useParams();
  const resto = (params["*"] ?? "").split("/").filter(Boolean);
  const secao = resto[0] ?? "";

  // O container mora AQUI, no despacho, e não em cada tela: assim as quatro (a
  // entrada e as três calculadoras) herdam a mesma largura sem repetição.
  //
  // A `Moldura` de `Ferramenta.tsx` não tem largura própria, e o `<Rodape />`
  // que ela desenha vive dentro de um `mx-auto max-w-6xl px-4`. Sem esta linha o
  // conteúdo encosta nas duas bordas de 1.280 px enquanto o rodapé fica
  // centrado — a página com dois alinhamentos, que foi o que Victor viu na tela
  // em 14/09. É a cicatriz do "Rodape solto no flex" pelo lado inverso, e a
  // mesma que o Leitor de Processos e o Radar Simples já pagaram.
  return <div className="mx-auto w-full max-w-6xl px-4 sm:px-6">{telaDaSecao(secao)}</div>;
}

function Entrada() {
  const [historico, setHistorico] = useState<Historico | null>(null);

  useEffect(() => {
    let vivo = true;
    void meusCalculos().then((r) => {
      if (!vivo) return;
      if (r !== null && "calculos" in r) setHistorico(r);
    });
    return () => {
      vivo = false;
    };
  }, []);

  return (
    <>
      <Cabine
        rotulo="Ferramenta"
        titulo="Calculadoras da Reforma"
        descricao={
          <>
            As três calculadoras da Reforma Tributária, ligadas à{" "}
            <strong className="text-white">calculadora oficial da Receita Federal</strong>. O
            número que aparece aqui é o que a Receita respondeu — o hub preenche, envia e
            explica, mas não calcula por conta própria.
          </>
        }
        fatos={
          historico && historico.total > 0 ? (
            <div className="rounded-cf-lg bg-white/10 p-4">
              <p className="text-xs font-bold tracking-widest text-navy-mute uppercase">
                Guardados por você
              </p>
              <p className="mt-1 font-display text-2xl font-bold text-white tabular-nums">
                {historico.total}
              </p>
              <p className="mt-0.5 text-xs text-navy-mute">
                {historico.total === 1 ? "cálculo" : "cálculos"} no seu histórico
              </p>
            </div>
          ) : undefined
        }
      />

      <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {PORTAS.map((p) => (
          <Link
            key={p.seg}
            to={`${BASE}/${p.seg}${window.location.search}`}
            className="surface porta rise flex flex-col rounded-cf-lg p-5"
          >
            <h2 className="flex items-center gap-2 font-display text-base font-bold text-titulo">
              {p.nome}
              <SetaDireita className="shrink-0 text-laranja" />
            </h2>
            <p className="mt-2 text-sm leading-relaxed text-cinza">{p.resumo}</p>
          </Link>
        ))}
      </div>

      <ListaDoHistorico historico={historico} aoMudar={setHistorico} />

      <p className="mt-8 rounded-xl bg-superficie-2 p-4 text-xs leading-relaxed text-cinza">
        Os cálculos vêm da calculadora de tributos sobre o consumo da Receita Federal, em{" "}
        <strong className="text-tinta">ambiente de produção restrita</strong> (piloto). Boa
        parte das alíquotas da Reforma ainda não foi fixada em lei, e a Receita marca esses
        casos na resposta — quando isso acontece, o aviso aparece ao lado do resultado. Não
        use um número marcado como simulado para apuração ou para orientar cliente sem
        conferir a norma.
      </p>
    </>
  );
}

/**
 * Os últimos cálculos guardados.
 *
 * A lista aparece na ENTRADA, e não dentro de cada calculadora, porque o aluno
 * costuma voltar procurando "aquele cálculo que fiz semana passada" sem lembrar
 * em qual das três ele foi feito.
 *
 * Os dois ramos estão escritos: com histórico, a lista; sem histórico, nada —
 * e "nada" aqui é a ausência do bloco inteiro, não uma caixa dizendo "0
 * cálculos". Zero grande é ruído, e a mesma decisão está na `FilaDeAvisos` do
 * admin.
 */
function ListaDoHistorico({
  historico,
  aoMudar,
}: {
  historico: Historico | null;
  aoMudar: (h: Historico) => void;
}) {
  const [apagando, setApagando] = useState<string | null>(null);
  const [erroEm, setErroEm] = useState<string | null>(null);

  if (!historico || historico.calculos.length === 0) return null;

  async function apagar(c: CalculoGuardado) {
    setApagando(c.id);
    setErroEm(null);
    const ok = await apagarCalculo(c.id);
    setApagando(null);
    if (!ok) {
      // Erro de UMA linha fica NA LINHA. Trocar a lista inteira por "não
      // conseguimos carregar" apagaria da tela os outros cálculos, que estão
      // certos — a cicatriz do `alternarFeito` do Calendário.
      setErroEm(c.id);
      return;
    }
    aoMudar({
      ...historico!,
      total: historico!.total - 1,
      calculos: historico!.calculos.filter((x) => x.id !== c.id),
    });
  }

  return (
    <section className="mt-8" aria-labelledby="titulo-historico">
      <h2 id="titulo-historico" className="font-display text-lg font-bold text-titulo">
        Seus cálculos
      </h2>
      <p className="mt-1 text-sm text-cinza">
        {historico.total > historico.calculos.length
          ? `Os ${historico.calculos.length} mais recentes de ${historico.total}.`
          : "Do mais recente para o mais antigo."}
      </p>

      <ul className="mt-4 divide-y divide-borda rounded-cf-lg border border-borda bg-superficie">
        {historico.calculos.map((c) => (
          <li key={c.id} className="flex flex-wrap items-center gap-3 p-4">
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-semibold text-tinta">
                {c.rotulo ?? nomeDaCalculadora(c.calculadora)}
              </p>
              <p className="mt-0.5 text-xs text-cinza">
                {nomeDaCalculadora(c.calculadora)} · {dataEmTexto(c.criado_em)}
                {c.simulado !== null && " · simulado"}
              </p>
            </div>
            <span className="text-sm font-bold text-titulo tabular-nums">
              {resumoDoResultado(c)}
            </span>
            <button
              type="button"
              className="rounded-lg border border-borda px-3 py-1.5 text-xs font-semibold text-cinza transition hover:border-erro-tinta hover:text-erro-tinta disabled:opacity-50"
              disabled={apagando === c.id}
              onClick={() => void apagar(c)}
            >
              {apagando === c.id ? "Apagando…" : "Apagar"}
            </button>
            {erroEm === c.id && (
              <p className="w-full text-xs text-erro-tinta">
                Não conseguimos apagar agora. Tente de novo — os outros cálculos continuam
                aqui.
              </p>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}

function nomeDaCalculadora(c: string): string {
  return PORTAS.find((p) => p.seg === c)?.nome ?? c;
}

function dataEmTexto(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit", year: "numeric" });
}

/**
 * O número que resume um cálculo guardado, na lista.
 *
 * Cada calculadora tem o SEU: no Regime Geral o que interessa é o total de
 * tributos, no Simplificado é a retenção, e na Base de Cálculo é a base. Um
 * campo só para os três mostraria travessão em dois deles — e uma linha de
 * histórico sem número não ajuda ninguém a se achar.
 */
function resumoDoResultado(c: CalculoGuardado): string {
  const r = c.resultado as Record<string, unknown>;
  if (c.rota === "split-payment") return emReais(r.valorTotalTributos);
  if (c.rota === "base-cbs-ibs" || c.rota === "base-is") return emReais(r.baseCalculo);

  // Regime Geral: a soma do total do ROC. Aqui vale o campo `IBSCBSTot` do gov,
  // e não a soma das linhas: no histórico não há linha na tela para o número
  // ter de bater com ela.
  const total = r.total as Record<string, unknown> | undefined;
  const trib = total?.tribCalc as Record<string, unknown> | undefined;
  const tot = trib?.IBSCBSTot as Record<string, unknown> | undefined;
  const gIBS = tot?.gIBS as Record<string, unknown> | undefined;
  const gCBS = tot?.gCBS as Record<string, unknown> | undefined;
  const istot = trib?.ISTot as Record<string, unknown> | undefined;
  const soma = [gIBS?.vIBS, gCBS?.vCBS, istot?.vIS]
    .map(numeroDoGov)
    .filter((v): v is number => v !== null);
  return soma.length > 0 ? emReais(soma.reduce((a, b) => a + b, 0)) : "—";
}
